import { Box } from '@mui/material';
import { Suspense, useMemo } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';

import { Approvals } from 'pages/Approvals/Approvals';
import { CreateApprovalRule } from 'pages/Approvals/CreateApprovalRule';
import { EditApprovalRule } from 'pages/Approvals/EditApprovalRule';
import { Budgeting } from 'pages/Budgeting/Budgeting';
import { AddCompany } from 'pages/Companies/AddCompany';
import { CompanyDetails } from 'pages/Companies/CompanyDetails';
import { AddCounterparty } from 'pages/Counterparties/AddCounterparty';
import { EditCounterparty } from 'pages/Counterparties/EditCounterparty';
import { Dashboard } from 'pages/Dashboard.tsx/Dashboard';
import { Expenses } from 'pages/Expenses/Expenses';
import { ExpenseTypesDataGrid } from 'pages/ExpenseTypes/ExpenseTypesDataGrid';
import { Payroll } from 'pages/Payroll/Payroll';
import { PromptBasedReporting } from 'pages/PromptBasedReporting/PromptBasedReporting';
import { Revenue } from 'pages/Revenue/Revenue';
import { UserManagement } from 'pages/UserManagement/UserManagement';

import { CompaniesCounterparties } from 'components/Counterparties/CompaniesCounterparties';
import { InvoiceVerification } from 'components/forms/InvoiceVerification/InvoiceVerification';
import { ProtectedRoute } from 'components/Navigation/ProtectedRoute';
import { Loader } from 'components/shared/Loader/Loader';

import { useCompanies } from 'context/CompanyContext';
import { usePermissions } from 'context/PermissionsContext';

import { useRouteSecurity } from 'hooks/useRouteSecurity';

import { FilterType } from 'utils/enums/FilterType';
import { AppRoutesEnum } from 'utils/routes';

import {
  routesWrapper,
  routesWrapperWithoutPadding
} from 'styles/components/AppStyle';

export const AppRoutes = () => {
  const { getHomePageRoute } = useRouteSecurity();
  const { companiesList, isCompaniesListLoading } = useCompanies();
  const { permissions } = usePermissions();
  const location = useLocation();

  const homePageRoute = useMemo(() => {
    return location.pathname === AppRoutesEnum.HOME
      ? getHomePageRoute()
      : AppRoutesEnum.HOME;
  }, [getHomePageRoute, location.pathname, companiesList]);

  return (
    permissions &&
    !isCompaniesListLoading && (
      <Box
        sx={
          location.pathname === AppRoutesEnum.PROMPT_BASED_REPORTING ||
          location.pathname === AppRoutesEnum.COMPANY_PROMPT_BASED_REPORTING
            ? routesWrapperWithoutPadding
            : routesWrapper
        }
      >
        <Suspense fallback={<Loader />}>
          <Routes>
            <Route
              path={AppRoutesEnum.HOME}
              element={
                <ProtectedRoute path={AppRoutesEnum.HOME}>
                  <Navigate to={homePageRoute} replace />
                </ProtectedRoute>
              }
            />
            <Route
              path={AppRoutesEnum.INVOICES}
              element={
                <ProtectedRoute path={AppRoutesEnum.INVOICES}>
                  <Expenses filterType={FilterType.Initial} isOnAllCompanies />
                </ProtectedRoute>
              }
            />

            <Route
              path={AppRoutesEnum.INVOICES_APPROVAL}
              element={
                <ProtectedRoute path={AppRoutesEnum.INVOICES_APPROVAL}>
                  <Expenses isOnAllCompanies filterType={FilterType.Approval} />
                </ProtectedRoute>
              }
            />

            <Route
              path={AppRoutesEnum.INVOICES_ACCOUNTING}
              element={
                <ProtectedRoute path={AppRoutesEnum.INVOICES_ACCOUNTING}>
                  <Expenses
                    filterType={FilterType.Accounting}
                    isOnAllCompanies
                  />
                </ProtectedRoute>
              }
            />

            <Route
              path={AppRoutesEnum.INVOICES_PAYMENT}
              element={
                <ProtectedRoute path={AppRoutesEnum.INVOICES_PAYMENT}>
                  <Expenses filterType={FilterType.Payment} isOnAllCompanies />
                </ProtectedRoute>
              }
            />

            <Route
              path={AppRoutesEnum.COMPANY_INVOICES}
              element={
                <ProtectedRoute path={AppRoutesEnum.COMPANY_INVOICES}>
                  <Expenses filterType={FilterType.Initial} />
                </ProtectedRoute>
              }
            />

            <Route
              path={AppRoutesEnum.COMPANY_INVOICES_APPROVAL}
              element={
                <ProtectedRoute path={AppRoutesEnum.COMPANY_INVOICES_APPROVAL}>
                  <Expenses filterType={FilterType.Approval} />
                </ProtectedRoute>
              }
            />
            <Route
              path={AppRoutesEnum.COMPANY_INVOICES_ACCOUNTING}
              element={
                <ProtectedRoute
                  path={AppRoutesEnum.COMPANY_INVOICES_ACCOUNTING}
                >
                  <Expenses filterType={FilterType.Accounting} />
                </ProtectedRoute>
              }
            />
            <Route
              path={AppRoutesEnum.COMPANY_INVOICES_PAYMENT}
              element={
                <ProtectedRoute path={AppRoutesEnum.COMPANY_INVOICES_PAYMENT}>
                  <Expenses filterType={FilterType.Payment} />
                </ProtectedRoute>
              }
            />

            <Route
              path={AppRoutesEnum.REVENUE}
              element={
                <ProtectedRoute path={AppRoutesEnum.REVENUE}>
                  <Revenue filterType={FilterType.Initial} isOnAllCompanies />
                </ProtectedRoute>
              }
            />
            <Route
              path={AppRoutesEnum.COMPANY_REVENUE}
              element={
                <ProtectedRoute path={AppRoutesEnum.COMPANY_REVENUE}>
                  <Revenue filterType={FilterType.Initial} />
                </ProtectedRoute>
              }
            />
            <Route
              path={AppRoutesEnum.REVENUE_VERIFICATION}
              element={
                <ProtectedRoute path={AppRoutesEnum.REVENUE_VERIFICATION}>
                  <InvoiceVerification />
                </ProtectedRoute>
              }
            />

            <Route
              path={AppRoutesEnum.DASHBOARD}
              element={
                <ProtectedRoute path={AppRoutesEnum.DASHBOARD}>
                  <Dashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path={AppRoutesEnum.PROMPT_BASED_REPORTING}
              element={
                <ProtectedRoute path={AppRoutesEnum.DASHBOARD}>
                  <PromptBasedReporting />
                </ProtectedRoute>
              }
            />
            <Route
              path={AppRoutesEnum.COMPANY_PROMPT_BASED_REPORTING}
              element={
                <ProtectedRoute path={AppRoutesEnum.COMPANY_DASHBOARD}>
                  <PromptBasedReporting />
                </ProtectedRoute>
              }
            />
            <Route
              path={AppRoutesEnum.COMPANY_DASHBOARD}
              element={
                <ProtectedRoute path={AppRoutesEnum.COMPANY_DASHBOARD}>
                  <Dashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path={AppRoutesEnum.COMPANY_COUNTERPARTIES}
              element={
                <ProtectedRoute path={AppRoutesEnum.COMPANY_COUNTERPARTIES}>
                  <CompaniesCounterparties />
                </ProtectedRoute>
              }
            />
            <Route
              path={AppRoutesEnum.COMPANY_COUNTERPARTIES_NOT_VALIDATED}
              element={
                <ProtectedRoute
                  path={AppRoutesEnum.COMPANY_COUNTERPARTIES_NOT_VALIDATED}
                >
                  <CompaniesCounterparties />
                </ProtectedRoute>
              }
            />
            <Route
              path={AppRoutesEnum.COMPANY_COUNTERPARTIES_NEW}
              element={
                <ProtectedRoute path={AppRoutesEnum.COMPANY_COUNTERPARTIES_NEW}>
                  <AddCounterparty />
                </ProtectedRoute>
              }
            />
            <Route
              path={AppRoutesEnum.COMPANY_COUNTERPARTIES_EDIT}
              element={
                <ProtectedRoute
                  path={AppRoutesEnum.COMPANY_COUNTERPARTIES_EDIT}
                >
                  <EditCounterparty />
                </ProtectedRoute>
              }
            />
            <Route
              path={AppRoutesEnum.COMPANY_EXPENSE_TYPES}
              element={
                <ProtectedRoute path={AppRoutesEnum.COMPANY_EXPENSE_TYPES}>
                  <ExpenseTypesDataGrid />
                </ProtectedRoute>
              }
            />
            <Route
              path={AppRoutesEnum.COMPANY_APPROVAL_RULES}
              element={
                <ProtectedRoute path={AppRoutesEnum.COMPANY_APPROVAL_RULES}>
                  <Approvals />
                </ProtectedRoute>
              }
            />
            <Route
              path={AppRoutesEnum.COMPANY_APPROVAL_RULES_NEW}
              element={
                <ProtectedRoute path={AppRoutesEnum.COMPANY_APPROVAL_RULES_NEW}>
                  <CreateApprovalRule />
                </ProtectedRoute>
              }
            />
            <Route
              path={AppRoutesEnum.COMPANY_APPROVAL_RULES_EDIT}
              element={
                <ProtectedRoute
                  path={AppRoutesEnum.COMPANY_APPROVAL_RULES_EDIT}
                >
                  <EditApprovalRule />
                </ProtectedRoute>
              }
            />
            <Route
              path={AppRoutesEnum.COMPANY_BUDGETING}
              element={
                <ProtectedRoute path={AppRoutesEnum.COMPANY_BUDGETING}>
                  <Budgeting />
                </ProtectedRoute>
              }
            />
            <Route
              path={AppRoutesEnum.COMPANY_PAYROLL}
              element={
                <ProtectedRoute path={AppRoutesEnum.COMPANY_PAYROLL}>
                  <Payroll />
                </ProtectedRoute>
              }
            />
            <Route
              path={AppRoutesEnum.INVOICE_VERIFICATION}
              element={
                <ProtectedRoute path={AppRoutesEnum.INVOICE_VERIFICATION}>
                  <InvoiceVerification />
                </ProtectedRoute>
              }
            />

            <Route
              path={AppRoutesEnum.COMPANY_DETAILS}
              element={
                <ProtectedRoute path={AppRoutesEnum.COMPANY_DETAILS}>
                  <CompanyDetails />
                </ProtectedRoute>
              }
            />
            <Route
              path={AppRoutesEnum.ADD_COMPANY}
              element={
                <ProtectedRoute path={AppRoutesEnum.ADD_COMPANY}>
                  <AddCompany />
                </ProtectedRoute>
              }
            />

            <Route
              path={AppRoutesEnum.USER_MANAGEMENT}
              element={
                <ProtectedRoute path={AppRoutesEnum.USER_MANAGEMENT}>
                  <UserManagement />
                </ProtectedRoute>
              }
            />

            <Route
              path={AppRoutesEnum.COMPANY_USER_MANAGEMENT}
              element={
                <ProtectedRoute path={AppRoutesEnum.COMPANY_USER_MANAGEMENT}>
                  <UserManagement />
                </ProtectedRoute>
              }
            />

            <Route path="*" element={<Navigate to={homePageRoute} replace />} />
          </Routes>
        </Suspense>
      </Box>
    )
  );
};
